// Estado global del chat de Atlas.
//
// Un ÚNICO motor vive acá arriba del árbol, así el panel lateral y la pantalla
// dedicada comparten la misma conversación: si escribís en el panel y después
// abrís la pantalla completa, seguís donde estabas (mensajes, interruptores,
// borrador del input y el turno en curso, todo).
//
// Cualquier pantalla abre el chat con `abrirChat({ contexto })`, donde
// `contexto` es una nota de dónde se abrió — se le pasa al agente en el bloque
// volátil del system prompt para que sepa desde qué pantalla lo llamaron.
//
// ── POR QUÉ SON DOS CONTEXTOS Y NO UNO ────────────────────────────────────
// La conversación cambia de identidad en CADA token del stream (`mensajes` es
// un array nuevo por fragmento). Con un solo contexto, todo el que lo leyera se
// volvía a renderizar decenas de veces por segundo — y uno de los lectores es
// `useEmpujeChat`, que lo llama `DashboardLayout`: el cascarón entero, con la
// `<Sidebar />` (1500 líneas, sin memo) y la nav mobile adentro, re-renderizando
// mientras Atlas escribe.
//
// Por eso la parte de APERTURA/LAYOUT (abierto, vista, contexto) vive en su
// propio contexto, que sólo cambia cuando el chat se abre, se cierra o cambia de
// vista. `DashboardLayout` lee ese y nada más. El contexto grande —con la
// conversación— lo leen únicamente los componentes del chat, que sí tienen que
// repintarse con cada token.

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useMotorChatAtlas } from './useMotorChatAtlas';
import type {
  AdjuntoChat,
  ContextoApertura,
  InterruptoresChat,
  MensajeChatAtlas,
} from './tipos';

export type VistaChat = 'panel' | 'pantalla';

export interface OpcionesApertura {
  /** Desde dónde se abre (pantalla, ruta, qué está mirando). */
  contexto?: ContextoApertura | null;
  /** Panel lateral (default) o pantalla dedicada. */
  vista?: VistaChat;
  /** Texto para dejar precargado en el input. NO se envía solo. */
  mensajeInicial?: string;
}

/** Cómo se llegó a la pantalla dedicada. Los dos caminos son distintos y hasta
 *  ahora se trataban igual, con dos efectos molestos:
 *   • expandir el panel con el botón de maximizar BORRABA el contexto de
 *     apertura, así que el agente dejaba de saber qué es "esto" o "acá";
 *   • entrar por la sidebar dejaba el panel ABIERTO, y al ir a cualquier otra
 *     pantalla aparecía una franja de 420px que el usuario nunca pidió. */
export interface EntradaAPantalla {
  /** true si se viene de expandir el panel (hay una conversación en curso con
   *  su contexto, y el panel tiene que seguir abierto al volver). */
  conservarContexto: boolean;
}

/** La parte del estado que mira el LAYOUT. Cambia poco y a propósito: es lo que
 *  evita que el cascarón se re-renderice con cada token. */
export interface CascaronChatAtlas {
  abierto: boolean;
  vista: VistaChat;
  contexto: ContextoApertura | null;
  abrirChat: (opciones?: OpcionesApertura) => void;
  cerrarChat: () => void;
  cambiarVista: (vista: VistaChat) => void;
  /** Llegada a `/chat`. Ver `EntradaAPantalla`. */
  entrarAPantalla: (entrada: EntradaAPantalla) => void;
}

export interface ValorChatAtlas extends CascaronChatAtlas {
  // ── apertura ────────────────────────────────────────────────────────
  /** Borrador del input, compartido entre panel y pantalla completa. */
  borrador: string;
  setBorrador: (texto: string) => void;
  /** Adjuntos enganchados y todavía sin mandar. También compartidos: si
   *  enganchás una foto en el panel y pasás a la pantalla, sigue ahí. */
  adjuntos: AdjuntoChat[];
  setAdjuntos: React.Dispatch<React.SetStateAction<AdjuntoChat[]>>;
  // ── respuestas gráficas ─────────────────────────────────────────────
  /** Id del gráfico que se está mirando en el panel de la derecha, o null.
   *
   *  Vive ACÁ y no en la pantalla dedicada porque el viaje empieza en el panel
   *  lateral: ahí no hay ancho para dibujar un gráfico, así que la tarjeta
   *  marca cuál abrir y recién después navega a `/chat`. Si el id fuera estado
   *  local de la pantalla, ese salto lo perdería y el usuario aterrizaría en
   *  una pantalla que no muestra lo que pidió. */
  graficoActivoId: string | null;
  abrirGrafico: (id: string) => void;
  cerrarGrafico: () => void;
  // ── conversación ────────────────────────────────────────────────────
  mensajes: MensajeChatAtlas[];
  streaming: boolean;
  actividad: string | null;
  interruptores: InterruptoresChat;
  setInterruptor: (clave: keyof InterruptoresChat, valor: boolean) => void;
  /** Envía el texto (más los adjuntos enganchados) con el contexto de apertura
   *  vigente. Limpia borrador y adjuntos. */
  enviarMensaje: (texto: string) => void;
  detener: () => void;
  reiniciar: () => void;
}

const ChatAtlasContext = createContext<ValorChatAtlas | undefined>(undefined);
const CascaronContext = createContext<CascaronChatAtlas | undefined>(undefined);

export const ChatAtlasProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const motor = useMotorChatAtlas();
  const [abierto, setAbierto] = useState(false);
  const [vista, setVista] = useState<VistaChat>('panel');
  const [contexto, setContexto] = useState<ContextoApertura | null>(null);
  const [borrador, setBorrador] = useState('');
  const [adjuntos, setAdjuntos] = useState<AdjuntoChat[]>([]);
  const [graficoActivoId, setGraficoActivoId] = useState<string | null>(null);

  // Espejo del contexto para no recrear `enviarMensaje` en cada apertura.
  const contextoRef = useRef<ContextoApertura | null>(null);
  contextoRef.current = contexto;
  // Ídem con los adjuntos: si `enviarMensaje` los tomara del estado, cambiaría
  // de identidad con cada archivo enganchado y remontaría medio árbol de la UI.
  const adjuntosRef = useRef<AdjuntoChat[]>([]);
  adjuntosRef.current = adjuntos;

  const abrirChat = useCallback((opciones?: OpcionesApertura) => {
    if (opciones?.contexto !== undefined) setContexto(opciones.contexto);
    if (opciones?.vista) setVista(opciones.vista);
    if (opciones?.mensajeInicial !== undefined) setBorrador(opciones.mensajeInicial);
    setAbierto(true);
  }, []);

  // Cerrar NO borra la conversación: al reabrir el chat sigue donde estaba.
  const cerrarChat = useCallback(() => setAbierto(false), []);
  const cambiarVista = useCallback((v: VistaChat) => setVista(v), []);

  const entrarAPantalla = useCallback(({ conservarContexto }: EntradaAPantalla) => {
    setVista('pantalla');
    if (conservarContexto) {
      // Se expandió una conversación que ya venía de algún lado: el contexto de
      // apertura sigue valiendo y el panel queda abierto para cuando el usuario
      // vuelva a otra pantalla.
      setAbierto(true);
      return;
    }
    // Entrada fría (sidebar o URL pegada): no hay ningún "acá" del que hablar,
    // y tampoco había un panel abierto — así que no se abre uno.
    setContexto(null);
    setAbierto(false);
  }, []);

  const abrirGrafico = useCallback((id: string) => setGraficoActivoId(id), []);
  const cerrarGrafico = useCallback(() => setGraficoActivoId(null), []);

  const enviarMensaje = useCallback(
    (texto: string) => {
      const enganchados = adjuntosRef.current;
      motor.enviarMensaje(texto, contextoRef.current, enganchados);
      setBorrador('');
      if (enganchados.length > 0) setAdjuntos([]);
    },
    [motor],
  );

  // Empezar de cero es empezar de cero: además de la conversación se van el
  // borrador y los adjuntos que todavía estaban enganchados (si no, el primer
  // mensaje del chat "nuevo" se llevaba puesta la foto del anterior) y el
  // gráfico abierto, cuyos mensajes ya no existen.
  const reiniciar = useCallback(() => {
    motor.reiniciar();
    setGraficoActivoId(null);
    setBorrador('');
    setAdjuntos([]);
  }, [motor]);

  const cascaron = useMemo<CascaronChatAtlas>(
    () => ({
      abierto,
      vista,
      contexto,
      abrirChat,
      cerrarChat,
      cambiarVista,
      entrarAPantalla,
    }),
    [abierto, vista, contexto, abrirChat, cerrarChat, cambiarVista, entrarAPantalla],
  );

  const valor = useMemo<ValorChatAtlas>(
    () => ({
      ...cascaron,
      borrador,
      setBorrador,
      adjuntos,
      setAdjuntos,
      graficoActivoId,
      abrirGrafico,
      cerrarGrafico,
      mensajes: motor.mensajes,
      streaming: motor.streaming,
      actividad: motor.actividad,
      interruptores: motor.interruptores,
      setInterruptor: motor.setInterruptor,
      enviarMensaje,
      detener: motor.detener,
      reiniciar,
    }),
    [
      cascaron,
      borrador,
      adjuntos,
      graficoActivoId,
      abrirGrafico,
      cerrarGrafico,
      motor,
      enviarMensaje,
      reiniciar,
    ],
  );

  return (
    <CascaronContext.Provider value={cascaron}>
      <ChatAtlasContext.Provider value={valor}>{children}</ChatAtlasContext.Provider>
    </CascaronContext.Provider>
  );
};

/** Estado del chat de Atlas. Tira error si falta el provider (mejor eso que un
 *  botón que no hace nada). */
export const useChatAtlas = (): ValorChatAtlas => {
  const ctx = useContext(ChatAtlasContext);
  if (!ctx) {
    throw new Error('useChatAtlas necesita estar dentro de <ChatAtlasProvider>');
  }
  return ctx;
};

/**
 * Sólo la parte de apertura/layout. ES LO QUE TIENE QUE USAR EL CASCARÓN
 * (`useEmpujeChat` → `DashboardLayout`): leer el contexto grande desde ahí
 * arriba re-renderiza la sidebar entera con cada token del stream.
 */
export const useChatAtlasCascaron = (): CascaronChatAtlas => {
  const ctx = useContext(CascaronContext);
  if (!ctx) {
    throw new Error('useChatAtlasCascaron necesita estar dentro de <ChatAtlasProvider>');
  }
  return ctx;
};
